import type { FC, ReactNode } from 'react';
import { Tabs } from 'antd';
import type { TabsProps } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

export interface TabbedResourceSiderItem {
    children: ReactNode;
    icon?: ReactNode;
    key: string;
    label: ReactNode;
}

interface TabbedResourceSiderProps {
    activeKey?: string;
    items: TabbedResourceSiderItem[];
    onChange?: (key: string) => void;
    size?: TabsProps['size'];
}

/** A backend-agnostic tabbed sider shell shared by resource detail views. */
const TabbedResourceSider: FC<TabbedResourceSiderProps> = ({ activeKey, items, onChange, size }) => {
    const { token } = useAntDesignTheme();

    return (
        <>
            <style>
                {`
                    .tabbed-resource-sider > .ant-tabs-content-holder {
                        flex: 1 1 0%;
                        min-height: 0;
                        overflow: hidden;
                    }

                    .tabbed-resource-sider > .ant-tabs-nav {
                        flex: none;
                    }

                    .tabbed-resource-sider > .ant-tabs-content-holder > .ant-tabs-content,
                    .tabbed-resource-sider > .ant-tabs-content-holder > .ant-tabs-content > .ant-tabs-tabpane {
                        height: 100%;
                        min-height: 0;
                    }
                `}
            </style>
            <Tabs
                activeKey={activeKey}
                className='tabbed-resource-sider'
                destroyOnHidden={false}
                items={items.map((item) => ({
                    children: (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                                minHeight: 0,
                                overflow: 'hidden',
                            }}
                        >
                            {item.children}
                        </div>
                    ),
                    key: item.key,
                    label: (
                        <span style={{ alignItems: 'center', display: 'inline-flex', gap: '0.375rem' }}>
                            {item.icon}
                            {item.label}
                        </span>
                    ),
                }))}
                onChange={onChange}
                size={size}
                style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
                tabBarStyle={{
                    background: token.colorBgContainer,
                    margin: 0,
                    paddingInline: '0.75rem',
                }}
            />
        </>
    );
};

export default TabbedResourceSider;
